// =============================================================================
// SimSense Platform — Internal Admin Dashboard
// =============================================================================

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../utils/api";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../components/Toast";
import { useConfirm } from "../components/ConfirmDialog";
import { Select } from "../components/Select";
import { parseTicketBody, TicketAttachments } from "../components/TicketAttachments";

export function AdminDashboard() {
  const [trackModels, setTrackModels] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [coachingHistory, setCoachingHistory] = useState<any[]>([]);
  const [fleetSetups, setFleetSetups] = useState<any[]>([]);
  const [fleetStats, setFleetStats] = useState<any>(null);
  const [brands, setBrands] = useState<any[]>([]);
  const [brandForm, setBrandForm] = useState<{ name: string; website: string; keywords: string }>({ name: "", website: "", keywords: "" });
  const [showBrandForm, setShowBrandForm] = useState(false);
  const [editingBrandId, setEditingBrandId] = useState<number | null>(null);
  const [editingTrack, setEditingTrack] = useState<any>(null);
  const [trackCorners, setTrackCorners] = useState<any[]>([]);
  const [trackSaving, setTrackSaving] = useState(false);
  const [trackRefImage, setTrackRefImage] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedUserSessions, setSelectedUserSessions] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [queueHealth, setQueueHealth] = useState<any>(null);
  const [outdatedSessions, setOutdatedSessions] = useState<any>(null);
  
  // Release Notes [OT-131]
  const [releaseNotes, setReleaseNotes] = useState<any[]>([]);
  const [newNote, setNewNote] = useState<any>({
    title: "",
    body: "",
    version: "",
    type: "update"
  });

  // Weekly Challenges [OT-106]
  const [challenges, setChallenges] = useState<any[]>([]);
  const [newChallenge, setNewChallenge] = useState<any>({
    title: "",
    description: "",
    trackName: "",
    carName: "",
    startAt: new Date().toISOString().split('T')[0],
    endAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    isActive: true
  });

  // Support Tickets [OT-223B]
  const [supportTickets, setSupportTickets] = useState<any[]>([]);
  const [editingTicket, setEditingTicket] = useState<string | null>(null);
  const [ticketEdits, setTicketEdits] = useState<any>({});
  const [draftEdits, setDraftEdits] = useState<Record<string, string>>({});
  const [draftActionId, setDraftActionId] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [userSearch, setUserSearch] = useState("");
  const [activeTab, setActiveTab] = useState<"tracks" | "users" | "challenges" | "setups" | "brands" | "coaching" | "support" | "queue" | "reprocess" | "notes">("tracks");
  const { user: authUser } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();

  useEffect(() => {
    if (!authUser) return; // Wait for auth to resolve before fetching
    async function fetchAdminData() {
      setLoading(true);
      try {
        const [tData, uData, cData, sData, bData, stData, jData, rData, chData, nData, qhData, stktData] = await Promise.all([
          api<any[]>("/api/admin/tracks"),
          api<any[]>("/api/admin/users"),
          api<any[]>("/api/admin/coaching/history"),
          api<any[]>("/api/admin/fleet/setups"),
          api<any[]>("/api/admin/commercial-brands"),
          api<any>("/api/admin/fleet/stats"),
          api<any[]>("/api/admin/jobs"),
          api<any>("/api/admin/reprocess/outdated").catch(() => null),
          api<any[]>("/api/challenges?active_only=false").catch(() => []),
          api<any[]>("/api/admin/release-notes").catch(() => []),
          api<any>("/api/admin/queue-health").catch(() => null),
          api<any[]>("/api/admin/support/tickets").catch(() => []),
        ]);

        setTrackModels(tData || []);
        setUsers(uData || []);
        setCoachingHistory(cData || []);
        setFleetSetups(sData || []);
        setBrands(bData || []);
        setFleetStats(stData);
        setJobs(jData || []);
        setOutdatedSessions(rData);
        setChallenges(chData || []);
        setReleaseNotes(nData || []);
        setQueueHealth(qhData);
        setSupportTickets(stktData || []);
      } catch (err) {
        console.error("Admin fetch failed", err);
      } finally {
        setLoading(false);
      }
    }
    fetchAdminData();
  }, [authUser]);

  const reprocessAll = async () => {
    const ok = await confirm({ title: "Bulk Reprocess", message: `Are you sure you want to reprocess all ${outdatedSessions?.count} sessions?`, danger: true, confirmLabel: "Reprocess All" });
    if (!ok) return;
    try {
      await api("/api/admin/reprocess/all-outdated", { method: "POST" });
      toast.success("Bulk reprocessing queued.");
      const rData = await api<any>("/api/admin/reprocess/outdated");
      setOutdatedSessions(rData);
    } catch (err) {
      toast.error("Bulk reprocess failed.");
    }
  };

  const reprocessOne = async (id: string) => {
    try {
      await api(`/api/admin/reprocess/session/${id}`, { method: "POST" });
      toast.success("Reprocessing queued for this session.");
      const rData = await api<any>("/api/admin/reprocess/outdated");
      setOutdatedSessions(rData);
    } catch (err) {
      toast.error("Reprocess failed.");
    }
  };

  const restartJob = async (jobId: string) => {
    try {
      await api(`/api/admin/jobs/${jobId}/restart`, { method: "POST" });
      const updatedJobs = await api<any[]>("/api/admin/jobs");
      setJobs(updatedJobs);
    } catch (err) {
      toast.error("Restart failed.");
    }
  };

  const fetchUserSessions = async (user: any) => {
    setSelectedUser(user);
    setActiveTab("support");
    setLoading(true);
    try {
      const data = await api<any[]>(`/api/admin/users/${user.id}/sessions`);
      setSelectedUserSessions(data);
    } catch (err) {
      console.error("Failed to fetch user sessions", err);
    } finally {
      setLoading(false);
    }
  };

  const addBrand = async () => {
    const name = brandForm.name.trim();
    if (!name) return;
    try {
      const brand = await api<any>("/api/admin/commercial-brands", {
        method: "POST",
        body: JSON.stringify({ brandName: name })
      });
      setBrands([...brands, brand]);
      setBrandForm({ name: "", website: "", keywords: "" });
      setShowBrandForm(false);
      toast.success(`"${name}" registered as a protected brand.`);
    } catch (err) {
      toast.error("Failed to add brand. It might already exist.");
    }
  };

  const deleteBrand = async (id: number) => {
    try {
      await api(`/api/admin/commercial-brands/${id}`, { method: "DELETE" });
      setBrands(brands.filter(b => b.id !== id));
      toast.success("Brand removed.");
    } catch (err) {
      toast.error("Delete failed.");
    }
  };

  // Track Editor Actions
  const openTrackEditor = async (track: any) => {
    try {
      const detail = await api<any>(`/api/admin/tracks/${track.trackId}`);
      setEditingTrack(detail);
      setTrackCorners(detail.corners || []);
    } catch (err) {
      toast.error("Failed to load track details.");
    }
  };

  const saveTrackCorners = async () => {
    if (!editingTrack) return;
    setTrackSaving(true);
    try {
      const updated = await api<any>(`/api/admin/tracks/${editingTrack.trackId}`, {
        method: "PUT",
        body: JSON.stringify({
          trackId: editingTrack.trackId,
          trackName: editingTrack.trackName,
          corners: trackCorners,
          geometry: editingTrack.geometry,
          svgOutline: editingTrack.svgOutline,
        })
      });
      setTrackModels(trackModels.map(t => t.trackId === editingTrack.trackId ? updated : t));
      setEditingTrack(updated);
      toast.success("Track boundaries saved.");
    } catch (err) {
      toast.error("Failed to save track boundaries.");
    } finally {
      setTrackSaving(false);
    }
  };

  const generateTrackSvg = async (trackId: number) => {
    try {
      const updated = await api<any>(`/api/admin/tracks/${trackId}/generate-svg`, { method: "POST" });
      setTrackModels(trackModels.map(t => t.trackId === trackId ? updated : t));
      if (editingTrack?.trackId === trackId) setEditingTrack(updated);
      toast.success("SVG outline generated from telemetry.");
    } catch (err: any) {
      toast.error(err?.message || "Failed to generate SVG. Ensure sessions exist for this track.");
    }
  };

  const updateCorner = (index: number, field: string, value: any) => {
    setTrackCorners(prev => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };

  const addCorner = () => {
    setTrackCorners(prev => [...prev, { name: `Turn ${prev.length + 1}`, number: prev.length + 1, lapDistPctStart: 0, lapDistPctEnd: 0 }]);
  };

  const removeCorner = (index: number) => {
    setTrackCorners(prev => prev.filter((_, i) => i !== index));
  };

  // Challenge Actions
  const createChallenge = async () => {
    if (!newChallenge.title || !newChallenge.trackName) {
      toast.error("Title and Track Name are required.");
      return;
    }
    try {
      const challenge = await api<any>("/api/challenges", {
        method: "POST",
        body: JSON.stringify({
          ...newChallenge,
          startAt: new Date(newChallenge.startAt).toISOString(),
          endAt: new Date(newChallenge.endAt).toISOString()
        })
      });
      setChallenges([challenge, ...challenges]);
      toast.success("Challenge created successfully.");
    } catch (err) {
      toast.error("Failed to create challenge.");
    }
  };

  const toggleChallenge = async (id: string) => {
    try {
      const result = await api<any>(`/api/challenges/${id}/toggle`, { method: "POST" });
      setChallenges(challenges.map(c => c.id === id ? { ...c, isActive: result.isActive } : c));
    } catch (err) {
      toast.error("Failed to toggle challenge.");
    }
  };

  const deleteChallenge = async (id: string) => {
    const ok = await confirm({ title: "Delete Challenge", message: "Are you sure you want to delete this challenge?", danger: true, confirmLabel: "Delete" });
    if (!ok) return;
    try {
      await api(`/api/challenges/${id}`, { method: "DELETE" });
      setChallenges(challenges.filter(c => c.id !== id));
    } catch (err) {
      toast.error("Failed to delete challenge.");
    }
  };

  // Release Note Actions
  const createReleaseNote = async () => {
    if (!newNote.title || !newNote.body) {
      toast.error("Title and Body are required.");
      return;
    }
    try {
      const note = await api<any>("/api/admin/release-notes", {
        method: "POST",
        body: JSON.stringify(newNote)
      });
      setReleaseNotes([note, ...releaseNotes]);
      setNewNote({ title: "", body: "", version: "", type: "update" });
      toast.success("Draft release note created.");
    } catch (err) {
      toast.error("Failed to create note.");
    }
  };

  const publishReleaseNote = async (id: string, publish: boolean) => {
    try {
      const note = await api<any>(`/api/admin/release-notes/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ is_published: publish })
      });
      setReleaseNotes(releaseNotes.map(n => n.id === id ? note : n));
    } catch (err) {
      toast.error("Failed to update status.");
    }
  };

  const deleteReleaseNote = async (id: string) => {
    const ok = await confirm({ title: "Delete Note", message: "Are you sure you want to delete this release note?", danger: true, confirmLabel: "Delete" });
    if (!ok) return;
    try {
      await api(`/api/admin/release-notes/${id}`, { method: "DELETE" });
      setReleaseNotes(releaseNotes.filter(n => n.id !== id));
    } catch (err) {
      toast.error("Failed to delete.");
    }
  };

  // OT-223B: Support ticket management
  const updateTicket = async (ticketId: string) => {
    const edits = ticketEdits[ticketId];
    if (!edits) return;
    try {
      const updated = await api<any>(`/api/admin/support/tickets/${ticketId}`, {
        method: "PUT",
        body: JSON.stringify(edits)
      });
      setSupportTickets(supportTickets.map(t => t.id === ticketId ? updated : t));
      setEditingTicket(null);
      setTicketEdits({ ...ticketEdits, [ticketId]: undefined });
      toast.success("Ticket updated.");
    } catch (err) {
      toast.error("Failed to update ticket.");
    }
  };

  const deleteTicket = async (ticketId: string) => {
    const ok = await confirm({ title: "Delete Ticket", message: "Permanently delete this support ticket?", danger: true, confirmLabel: "Delete" });
    if (!ok) return;
    try {
      await api(`/api/admin/support/tickets/${ticketId}`, { method: "DELETE" });
      setSupportTickets(supportTickets.filter(t => t.id !== ticketId));
      toast.success("Ticket deleted.");
    } catch (err) {
      toast.error("Failed to delete ticket.");
    }
  };

  const startEditTicket = (ticket: any) => {
    setEditingTicket(ticket.id);
    setTicketEdits({
      ...ticketEdits,
      [ticket.id]: { status: ticket.status, priority: ticket.priority, adminNotes: ticket.adminNotes || "" }
    });
  };

  const approveDraft = async (ticketId: string, draft: any) => {
    if (draftActionId) return;
    setDraftActionId(draft.id);
    try {
      const result = await api<any>(`/api/admin/support/drafts/${draft.id}/approve-send`, {
        method: "POST",
        body: JSON.stringify({ body: (draftEdits[draft.id] ?? draft.body).trim() })
      });
      setSupportTickets(prev => prev.map(ticket => {
        if (ticket.id !== ticketId) return ticket;
        const nextDrafts = (ticket.drafts || []).map((existing: any) =>
          existing.id === draft.id ? result.draft : existing
        );
        return {
          ...ticket,
          drafts: nextDrafts,
          messages: [...(ticket.messages || []), result.message],
          status: result.ticketStatus,
        };
      }));
      setDraftEdits(prev => ({ ...prev, [draft.id]: undefined as unknown as string }));
      toast.success("Draft approved and sent.");
    } catch (err: any) {
      toast.error(err?.message || "Failed to send draft.");
    } finally {
      setDraftActionId(null);
    }
  };

  const rejectDraft = async (ticketId: string, draftId: string) => {
    if (draftActionId) return;
    setDraftActionId(draftId);
    try {
      const rejected = await api<any>(`/api/admin/support/drafts/${draftId}/reject`, {
        method: "POST",
      });
      setSupportTickets(prev => prev.map(ticket => {
        if (ticket.id !== ticketId) return ticket;
        return {
          ...ticket,
          drafts: (ticket.drafts || []).map((existing: any) =>
            existing.id === draftId ? rejected : existing
          ),
        };
      }));
      toast.success("Draft rejected.");
    } catch (err: any) {
      toast.error(err?.message || "Failed to reject draft.");
    } finally {
      setDraftActionId(null);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: "32px" }}>
        <h2>System Administration</h2>
        <p style={{ color: "var(--text2)", fontSize: "14px" }}>
          Platform infrastructure, community events, and security monitoring.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "20px", marginBottom: "32px" }}>
        <StatCard label="Total User Accounts" value={fleetStats?.total_users ?? fleetStats?.totalUsers ?? 0} />
        <StatCard label="Telemetry Sessions" value={fleetStats?.total_sessions ?? fleetStats?.totalSessions ?? 0} />
        <StatCard label="Platform Setups" value={fleetStats?.total_setups ?? fleetStats?.totalSetups ?? 0} />
        <StatCard label="AI Coaching Usage" value={fleetStats?.total_ai_reports ?? fleetStats?.totalAiReports ?? 0} />
        <StatCard label="Aggregate AI Cost" value={`$${(fleetStats?.total_ai_cost_usd ?? fleetStats?.totalAiCostUsd ?? 0).toFixed(2)}`} />
      </div>

      <div style={{ display: "flex", gap: "24px", marginBottom: "24px", borderBottom: "1px solid var(--border)", overflowX: "auto" }}>
        <Tab active={activeTab === "tracks"} onClick={() => setActiveTab("tracks")} label="Track Models" />
        <Tab active={activeTab === "users"} onClick={() => setActiveTab("users")} label="User Directory" />
        <Tab active={activeTab === "challenges"} onClick={() => setActiveTab("challenges")} label="Weekly Challenges" />
        <Tab active={activeTab === "notes"} onClick={() => setActiveTab("notes")} label="Release Notes" />
        <Tab active={activeTab === "setups"} onClick={() => setActiveTab("setups")} label="Setup Audit" />
        <Tab active={activeTab === "brands"} onClick={() => setActiveTab("brands")} label="Commercial Brands" />
        <Tab active={activeTab === "coaching"} onClick={() => setActiveTab("coaching")} label="Coaching Analytics" />
        <Tab active={activeTab === "support"} onClick={() => setActiveTab("support")} label="Support Tickets" />
        <Tab active={activeTab === "queue"} onClick={() => setActiveTab("queue")} label="Background Tasks" />
        <Tab active={activeTab === "reprocess"} onClick={() => setActiveTab("reprocess")} label="System Reprocessing" />
      </div>

      {loading ? (
        <div style={{ padding: "40px", textAlign: "center" }}>Loading admin data...</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          
          {activeTab === "notes" && (
            <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
              <h3 style={{ fontSize: "18px", marginBottom: "24px" }}>System Notifications</h3>

              {/* Create Note Form */}
              <div style={{ background: "rgba(255,255,255,0.02)", padding: "24px", borderRadius: "12px", border: "1px solid var(--border)", marginBottom: "32px" }}>
                <h4 style={{ marginBottom: "16px", fontSize: "12px", textTransform: "uppercase", color: "var(--text3)", letterSpacing: "0.5px" }}>Create New Entry</h4>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                  <div>
                    <label style={labelStyle}>Headline</label>
                    <input type="text" style={inputStyle} value={newNote.title} onChange={e => setNewNote({...newNote, title: e.target.value})} placeholder="e.g. Server Maintenance" />
                  </div>
                  <div>
                    <label style={labelStyle}>Version Tag (Optional)</label>
                    <input type="text" style={inputStyle} value={newNote.version} onChange={e => setNewNote({...newNote, version: e.target.value})} placeholder="e.g. 0.3.1" />
                  </div>
                  <div>
                    <label style={labelStyle}>Entry Category</label>
                    <Select
                      value={newNote.type}
                      onChange={v => setNewNote({...newNote, type: v})}
                      options={[
                        { value: "update", label: "Platform Update" },
                        { value: "alert", label: "System Alert" },
                        { value: "maintenance", label: "Maintenance" },
                      ]}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: "20px" }}>
                  <label style={labelStyle}>Entry Content (Supports multiple items with '+')</label>
                  <textarea 
                    style={{ ...inputStyle, minHeight: "100px", fontFamily: "inherit" }} 
                    value={newNote.body} 
                    onChange={e => setNewNote({...newNote, body: e.target.value})} 
                    placeholder="Enter items separated by newlines or '+' for bullet points..."
                  />
                </div>
                <button onClick={createReleaseNote} style={{ ...btnStyle, background: "var(--accent)", width: "100%", height: "38px", fontWeight: 700 }}>Queue Entry</button>
              </div>

              {/* Notes List */}
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "var(--text2)", borderBottom: "1px solid var(--border)" }}>
                    <th style={{ padding: "12px 8px" }}>Status</th>
                    <th style={{ padding: "12px 8px" }}>Category</th>
                    <th style={{ padding: "12px 8px" }}>Headline / Version</th>
                    <th style={{ padding: "12px 8px" }}>Created Date</th>
                    <th style={{ padding: "12px 8px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {releaseNotes.map(n => (
                    <tr key={n.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "12px 8px" }}>
                        <span style={{ 
                          padding: "2px 6px", 
                          borderRadius: "4px", 
                          fontSize: "10px", 
                          background: n.is_published ? "var(--good)" : "var(--border)",
                          color: "#fff",
                          textTransform: "uppercase",
                          fontWeight: 700
                        }}>{n.is_published ? "Live" : "Draft"}</span>
                      </td>
                      <td style={{ padding: "12px 8px" }}>
                        <span style={{ color: "var(--text2)", textTransform: "capitalize" }}>{n.type}</span>
                      </td>
                      <td style={{ padding: "12px 8px" }}>
                        <div style={{ fontWeight: 600 }}>{n.title}</div>
                        {n.version && <div style={{ fontSize: "11px", color: "var(--text3)" }}>v{n.version}</div>}
                      </td>
                      <td style={{ padding: "12px 8px", color: "var(--text2)" }}>{new Date(n.created_at).toLocaleDateString()}</td>
                      <td style={{ padding: "12px 8px", textAlign: "right" }}>
                        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                          <button onClick={() => publishReleaseNote(n.id, !n.is_published)} style={btnStyle}>
                            {n.is_published ? "Revert to Draft" : "Release Entry"}
                          </button>
                          <button onClick={() => deleteReleaseNote(n.id)} style={{ ...btnStyle, background: "rgba(248, 113, 113, 0.1)", color: "#f87171" }}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {releaseNotes.length === 0 && (
                    <tr><td colSpan={5} style={{ padding: "40px", textAlign: "center", color: "var(--text2)" }}>No notifications queued.</td></tr>
                  )}
                </tbody>
              </table>
            </section>
          )}

          {activeTab === "challenges" && (
            <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
              <h3 style={{ fontSize: "18px", marginBottom: "24px" }}>Competition Management</h3>

              {/* Create Challenge Form */}
              <div style={{ background: "rgba(255,255,255,0.02)", padding: "24px", borderRadius: "12px", border: "1px solid var(--border)", marginBottom: "32px" }}>
                <h4 style={{ marginBottom: "16px", fontSize: "12px", textTransform: "uppercase", color: "var(--text3)", letterSpacing: "0.5px" }}>Create Weekly Challenge</h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                  <div>
                    <label style={labelStyle}>Challenge Title</label>
                    <input type="text" style={inputStyle} value={newChallenge.title} onChange={e => setNewChallenge({...newChallenge, title: e.target.value})} placeholder="e.g. Week 1: Rockingham" />
                  </div>
                  <div>
                    <label style={labelStyle}>Target Track</label>
                    <input type="text" style={inputStyle} value={newChallenge.trackName} onChange={e => setNewChallenge({...newChallenge, trackName: e.target.value})} placeholder="Full iRacing Track Name" />
                  </div>
                  <div>
                    <label style={labelStyle}>Vehicle Constraint (Optional)</label>
                    <input type="text" style={inputStyle} value={newChallenge.carName} onChange={e => setNewChallenge({...newChallenge, carName: e.target.value})} placeholder="Any Car if empty" />
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", marginBottom: "20px" }}>
                  <div>
                    <label style={labelStyle}>Launch Date</label>
                    <input type="date" style={inputStyle} value={newChallenge.startAt} onChange={e => setNewChallenge({...newChallenge, startAt: e.target.value})} />
                  </div>
                  <div>
                    <label style={labelStyle}>Expiration Date</label>
                    <input type="date" style={inputStyle} value={newChallenge.endAt} onChange={e => setNewChallenge({...newChallenge, endAt: e.target.value})} />
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-end" }}>
                    <button onClick={createChallenge} style={{ ...btnStyle, background: "var(--accent)", width: "100%", height: "38px", fontWeight: 700 }}>Initialize Competition</button>
                  </div>
                </div>
              </div>

              {/* Challenges List */}
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "var(--text2)", borderBottom: "1px solid var(--border)" }}>
                    <th style={{ padding: "12px 8px" }}>Status</th>
                    <th style={{ padding: "12px 8px" }}>Event Details</th>
                    <th style={{ padding: "12px 8px" }}>Configuration</th>
                    <th style={{ padding: "12px 8px" }}>Active Period</th>
                    <th style={{ padding: "12px 8px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {challenges.map(c => (
                    <tr key={c.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "12px 8px" }}>
                        <span style={{ 
                          padding: "2px 6px", 
                          borderRadius: "4px", 
                          fontSize: "10px", 
                          background: c.isActive ? "var(--good)" : "var(--border)",
                          color: "#fff",
                          textTransform: "uppercase",
                          fontWeight: 700
                        }}>{c.isActive ? "Active" : "Hidden"}</span>
                      </td>
                      <td style={{ padding: "12px 8px" }}>
                        <div style={{ fontWeight: 600 }}>{c.title}</div>
                        <div style={{ fontSize: "11px", color: "var(--text3)" }}>{c.description || "No description"}</div>
                      </td>
                      <td style={{ padding: "12px 8px" }}>
                        <div>{c.trackName}</div>
                        <div style={{ fontSize: "11px", color: "var(--text2)" }}>{c.carName || "Open Vehicle Class"}</div>
                      </td>
                      <td style={{ padding: "12px 8px", color: "var(--text2)" }}>
                        {new Date(c.startAt).toLocaleDateString()} — {new Date(c.endAt).toLocaleDateString()}
                      </td>
                      <td style={{ padding: "12px 8px", textAlign: "right" }}>
                        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                          <button onClick={() => toggleChallenge(c.id)} style={btnStyle}>{c.isActive ? "Hide" : "Activate"}</button>
                          <button onClick={() => deleteChallenge(c.id)} style={{ ...btnStyle, background: "rgba(248, 113, 113, 0.1)", color: "#f87171" }}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {challenges.length === 0 && (
                    <tr><td colSpan={5} style={{ padding: "40px", textAlign: "center", color: "var(--text2)" }}>No challenges created yet.</td></tr>
                  )}
                </tbody>
              </table>
            </section>
          )}

          {activeTab === "reprocess" && (
            <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <div>
                  <h3 style={{ fontSize: "18px" }}>System Reprocessing</h3>
                  <p style={{ color: "var(--text2)", fontSize: "12px" }}>
                    Identify and re-run sessions processed with legacy engine versions (Target: <strong>v{outdatedSessions?.current_version}</strong>).
                  </p>
                </div>
                {outdatedSessions?.count > 0 && (
                  <button onClick={reprocessAll} style={{ ...btnStyle, background: "var(--accent)" }}>Execute Bulk Reprocess ({outdatedSessions.count})</button>
                )}
              </div>

              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "var(--text2)", borderBottom: "1px solid var(--border)" }}>
                    <th style={{ padding: "12px 8px" }}>Session Reference</th>
                    <th style={{ padding: "12px 8px" }}>Environment</th>
                    <th style={{ padding: "12px 8px" }}>Legacy Version</th>
                    <th style={{ padding: "12px 8px" }}>Original Date</th>
                    <th style={{ padding: "12px 8px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {outdatedSessions?.sessions?.map((s: any) => (
                    <tr key={s.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "12px 8px" }}>
                        <div style={{ fontWeight: 600 }}>{s.id.substring(0, 8)}...</div>
                        <div style={{ fontSize: "10px", color: "var(--text3)" }}>{s.driver_name}</div>
                      </td>
                      <td style={{ padding: "12px 8px" }}>
                        <div>{s.track_name}</div>
                        <div style={{ fontSize: "10px", color: "var(--text2)" }}>{s.car_name}</div>
                      </td>
                      <td style={{ padding: "12px 8px" }}>
                        <span style={{ color: "var(--bad)" }}>v{s.parser_version || 0}</span>
                      </td>
                      <td style={{ padding: "12px 8px", color: "var(--text2)" }}>{new Date(s.session_date).toLocaleDateString()}</td>
                      <td style={{ padding: "12px 8px", textAlign: "right" }}>
                        <button onClick={() => reprocessOne(s.id)} style={btnStyle}>Reprocess</button>
                      </td>
                    </tr>
                  ))}
                  {(!outdatedSessions || outdatedSessions.sessions?.length === 0) && (
                    <tr><td colSpan={5} style={{ padding: "40px", textAlign: "center", color: "var(--text2)" }}>All sessions are up to date with engine v{outdatedSessions?.current_version}.</td></tr>
                  )}
                </tbody>
              </table>
            </section>
          )}
          
          {activeTab === "setups" && (
            <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
              <h3 style={{ fontSize: "18px", marginBottom: "4px" }}>Setup Audit</h3>
              <p style={{ fontSize: "12px", color: "var(--text2)", marginBottom: "20px" }}>All setups detected across user telemetry uploads. Commercial setups are auto-flagged when their name matches a registered brand's keywords.</p>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "var(--text2)", borderBottom: "1px solid var(--border)" }}>
                    <th style={{ padding: "12px 8px" }}>Setup Name</th>
                    <th style={{ padding: "12px 8px" }}>Car</th>
                    <th style={{ padding: "12px 8px" }}>User</th>
                    <th style={{ padding: "12px 8px" }}>Status</th>
                    <th style={{ padding: "12px 8px" }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {fleetSetups.map(s => (
                    <tr key={s.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "12px 8px", fontWeight: 600 }}>{s.setupName}</td>
                      <td style={{ padding: "12px 8px" }}>{s.carName}</td>
                      <td style={{ padding: "12px 8px" }}>{s.userId.substring(0, 8)}...</td>
                      <td style={{ padding: "12px 8px" }}>
                        {s.isCommercial && (
                          <span style={{
                            fontSize: "10px", background: "rgba(245, 158, 11, 0.12)", color: "#fbbf24",
                            padding: "2px 6px", borderRadius: "3px", fontWeight: 600, marginRight: "8px",
                            border: "1px solid rgba(245, 158, 11, 0.2)",
                          }}>
                            &#x2606; {s.commercialBrand || "Commercial"}
                          </span>
                        )}
                        {s.isShared ? "Shared" : "Private"}
                      </td>
                      <td style={{ padding: "12px 8px", color: "var(--text2)" }}>{new Date(s.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {fleetSetups.length === 0 && (
                    <tr><td colSpan={5} style={{ padding: "20px", textAlign: "center", color: "var(--text2)" }}>No setups found.</td></tr>
                  )}
                </tbody>
              </table>
            </section>
          )}

          {activeTab === "brands" && (
            <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px" }}>
                <div>
                  <h3 style={{ fontSize: "18px", marginBottom: "4px" }}>Commercial Protection</h3>
                  <p style={{ color: "var(--text2)", fontSize: "12px", maxWidth: "560px" }}>
                    Register setup shops and their detection keywords. When a user uploads telemetry with a matching setup name,
                    it's automatically labeled and attributed to the brand. Shops can request specific keywords (e.g. "osc", "outlier") to be added.
                  </p>
                </div>
                <button onClick={() => { setShowBrandForm(true); setEditingBrandId(null); setBrandForm({ name: "", website: "", keywords: "" }); }} style={{ ...btnStyle, background: "var(--accent)", whiteSpace: "nowrap" }}>+ Register Brand</button>
              </div>

              {/* Add / Edit Brand Form */}
              {showBrandForm && (
                <div style={{
                  background: "rgba(59, 130, 246, 0.05)", border: "1px solid rgba(59, 130, 246, 0.2)",
                  borderRadius: "8px", padding: "20px", marginBottom: "24px",
                }}>
                  <h4 style={{ fontSize: "14px", marginBottom: "16px" }}>{editingBrandId ? "Edit Brand" : "Register New Brand"}</h4>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    <div>
                      <label style={{ display: "block", fontSize: "11px", color: "var(--text2)", fontWeight: 600, marginBottom: "4px" }}>Brand / Shop Name *</label>
                      <input
                        type="text"
                        placeholder="e.g. VRS, Craig's Setup Shop"
                        value={brandForm.name}
                        onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })}
                        style={{ width: "100%", background: "var(--bg)", border: "1px solid var(--border)", color: "#fff", padding: "8px 12px", borderRadius: "6px", fontSize: "13px" }}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: "11px", color: "var(--text2)", fontWeight: 600, marginBottom: "4px" }}>Website (optional)</label>
                      <input
                        type="url"
                        placeholder="https://setupshop.com"
                        value={brandForm.website}
                        onChange={(e) => setBrandForm({ ...brandForm, website: e.target.value })}
                        style={{ width: "100%", background: "var(--bg)", border: "1px solid var(--border)", color: "#fff", padding: "8px 12px", borderRadius: "6px", fontSize: "13px" }}
                      />
                    </div>
                  </div>
                  <div style={{ marginTop: "12px" }}>
                    <label style={{ display: "block", fontSize: "11px", color: "var(--text2)", fontWeight: 600, marginBottom: "4px" }}>
                      Detection Keywords
                      <span style={{ fontWeight: 400, marginLeft: "6px", color: "var(--text3)" }}>(comma-separated — the brand name is always matched automatically)</span>
                    </label>
                    <input
                      type="text"
                      placeholder='e.g. osc, outlier, outlier speed'
                      value={brandForm.keywords}
                      onChange={(e) => setBrandForm({ ...brandForm, keywords: e.target.value })}
                      style={{ width: "100%", background: "var(--bg)", border: "1px solid var(--border)", color: "#fff", padding: "8px 12px", borderRadius: "6px", fontSize: "13px" }}
                    />
                    <p style={{ fontSize: "11px", color: "var(--text3)", marginTop: "4px" }}>
                      These keywords are matched against setup names during IBT processing. The brand name itself is always included as a keyword.
                    </p>
                  </div>
                  <div style={{ marginTop: "12px" }}>
                    <label style={{ display: "block", fontSize: "11px", color: "var(--text2)", fontWeight: 600, marginBottom: "4px" }}>
                      Brand Logo
                      <span style={{ fontWeight: 400, marginLeft: "6px", color: "var(--text3)" }}>(displayed on matched setups)</span>
                    </label>
                    <div style={{
                      display: "flex", alignItems: "center", gap: "12px", padding: "12px",
                      background: "var(--bg)", borderRadius: "6px", border: "1px dashed var(--border)",
                    }}>
                      <div style={{
                        width: "48px", height: "48px", borderRadius: "6px",
                        background: "rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "20px", fontWeight: 700, color: "var(--text2)", flexShrink: 0,
                      }}>
                        {brandForm.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <p style={{ fontSize: "12px", color: "var(--text2)", marginBottom: "2px" }}>Logo upload available after backend storage is configured (OT-311).</p>
                        <p style={{ fontSize: "11px", color: "var(--text3)" }}>Until then, the brand initial is shown as a placeholder.</p>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                    <button onClick={addBrand} disabled={!brandForm.name.trim()} style={{
                      ...btnStyle, background: brandForm.name.trim() ? "var(--accent)" : "var(--border)",
                      cursor: brandForm.name.trim() ? "pointer" : "not-allowed", padding: "8px 20px",
                    }}>{editingBrandId ? "Save Changes" : "Register Brand"}</button>
                    <button onClick={() => { setShowBrandForm(false); setEditingBrandId(null); }} style={{ ...btnStyle, background: "transparent", border: "1px solid var(--border)" }}>Cancel</button>
                  </div>
                </div>
              )}

              {/* Brand Cards */}
              {brands.length === 0 && !showBrandForm ? (
                <div style={{
                  padding: "48px 24px", textAlign: "center", borderRadius: "8px",
                  border: "1px dashed var(--border)", background: "rgba(255,255,255,0.01)",
                }}>
                  <div style={{ fontSize: "32px", marginBottom: "12px", opacity: 0.3 }}>&#x1f6e1;</div>
                  <p style={{ color: "var(--text2)", fontSize: "14px", marginBottom: "4px" }}>No brands registered yet</p>
                  <p style={{ color: "var(--text3)", fontSize: "12px", maxWidth: "400px", margin: "0 auto" }}>
                    Register commercial setup brands to automatically detect and attribute them when users upload telemetry data.
                  </p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {brands.map(b => {
                    const matchCount = fleetSetups.filter(s => s.commercialBrand === b.brandName).length;
                    const keywords = (b.keywords || []).filter((k: string) => k !== b.brandName);
                    return (
                      <div key={b.id} style={{
                        display: "flex", alignItems: "center", gap: "16px", padding: "16px",
                        background: "rgba(255,255,255,0.02)", borderRadius: "8px", border: "1px solid var(--border)",
                      }}>
                        {/* Logo / Initial */}
                        {b.logoUrl ? (
                          <img src={b.logoUrl} alt="" style={{ width: "40px", height: "40px", borderRadius: "6px", objectFit: "cover", flexShrink: 0 }} />
                        ) : (
                          <div style={{
                            width: "40px", height: "40px", borderRadius: "6px", flexShrink: 0,
                            background: "rgba(59, 130, 246, 0.12)", display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: "16px", fontWeight: 700, color: "#60a5fa",
                          }}>
                            {b.brandName?.[0]?.toUpperCase() || "?"}
                          </div>
                        )}

                        {/* Info */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                            <span style={{ fontWeight: 600, fontSize: "14px" }}>{b.brandName}</span>
                            {b.websiteUrl && (
                              <a href={b.websiteUrl} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)", fontSize: "11px" }}>
                                {b.websiteUrl.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                              </a>
                            )}
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center" }}>
                            <span style={{
                              padding: "2px 8px", borderRadius: "4px", fontSize: "10px", fontWeight: 600,
                              background: "rgba(59, 130, 246, 0.15)", color: "#60a5fa",
                            }}>{b.brandName}</span>
                            {keywords.map((k: string) => (
                              <span key={k} style={{
                                padding: "2px 8px", borderRadius: "4px", fontSize: "10px",
                                background: "rgba(255,255,255,0.06)", color: "var(--text2)",
                              }}>{k}</span>
                            ))}
                            <span style={{ fontSize: "10px", color: "var(--text3)", marginLeft: "4px" }}>
                              {matchCount > 0 ? `${matchCount} matched setup${matchCount !== 1 ? "s" : ""}` : "No matches yet"}
                            </span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                          <button
                            onClick={async () => {
                              const ok = await confirm({ message: `Remove "${b.brandName}" from commercial brands? This will stop detecting setups with this brand name.` });
                              if (ok) deleteBrand(b.id);
                            }}
                            style={{ ...btnStyle, background: "rgba(248, 113, 113, 0.1)", color: "#f87171", fontSize: "11px" }}
                          >Remove</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* How it works */}
              <div style={{
                marginTop: "24px", padding: "16px", borderRadius: "8px",
                background: "rgba(255,255,255,0.02)", border: "1px solid var(--border)",
              }}>
                <h4 style={{ fontSize: "13px", marginBottom: "8px", color: "var(--text2)" }}>How Commercial Protection Works</h4>
                <ol style={{ fontSize: "12px", color: "var(--text3)", margin: 0, paddingLeft: "18px", lineHeight: 1.7 }}>
                  <li>Register a brand name and its detection keywords above.</li>
                  <li>When a user uploads telemetry, the IBT processor checks the setup name against all brand keywords.</li>
                  <li>Matching setups are attributed: <strong style={{ color: "var(--text2)" }}>"Official [Brand] setup"</strong> with the brand's identity.</li>
                  <li>Commercial setups are restricted from public sharing to protect paid content.</li>
                  <li>Shops can request additional keywords — add them via the brand registration form above.</li>
                </ol>
              </div>
            </section>
          )}

          {activeTab === "tracks" && (
            <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
                <div>
                  <h3 style={{ fontSize: "18px", marginBottom: "4px" }}>Track Models</h3>
                  <p style={{ fontSize: "12px", color: "var(--text2)" }}>Tracks are auto-detected from telemetry uploads. Edit corner boundaries to improve analysis accuracy.</p>
                </div>
              </div>

              {/* Track Editor Panel */}
              {editingTrack && (
                <div style={{
                  background: "rgba(59, 130, 246, 0.05)", border: "1px solid rgba(59, 130, 246, 0.2)",
                  borderRadius: "8px", padding: "20px", marginBottom: "24px",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                    <div>
                      <h4 style={{ fontSize: "15px", marginBottom: "2px" }}>{editingTrack.trackName}</h4>
                      <span style={{ fontSize: "11px", color: "var(--text2)" }}>Track ID: {editingTrack.trackId} | {trackCorners.length} corners</span>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button onClick={() => generateTrackSvg(editingTrack.trackId)} style={{ ...btnStyle, fontSize: "11px" }}>Generate SVG</button>
                      <button onClick={() => { setEditingTrack(null); setTrackCorners([]); }} style={{ ...btnStyle, background: "transparent", border: "1px solid var(--border)", fontSize: "11px" }}>Close</button>
                    </div>
                  </div>

                  {/* SVG Preview with optional reference image */}
                  <div style={{ marginBottom: "16px", padding: "12px", background: "var(--bg)", borderRadius: "6px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                      <span style={{ fontSize: "11px", color: "var(--text2)", fontWeight: 600 }}>Track Outline</span>
                      <label style={{ ...btnStyle, fontSize: "10px", padding: "3px 8px", cursor: "pointer", display: "inline-block" }}>
                        {trackRefImage ? "Change Reference" : "Load Reference Image"}
                        <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) { const url = URL.createObjectURL(file); setTrackRefImage(url); }
                          e.target.value = "";
                        }} />
                      </label>
                    </div>
                    <div style={{ position: "relative", width: "300px", height: "300px", margin: "0 auto", background: "rgba(0,0,0,0.3)", borderRadius: "4px", overflow: "hidden" }}>
                      {trackRefImage && (
                        <img src={trackRefImage} alt="Reference" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", opacity: 0.4 }} />
                      )}
                      {editingTrack.svgOutline ? (
                        <svg viewBox="0 0 400 400" width="300" height="300" style={{ position: "relative", zIndex: 1 }}>
                          <path d={editingTrack.svgOutline} fill="none" stroke="var(--accent)" strokeWidth="2.5" />
                        </svg>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--text3)", fontSize: "12px" }}>
                          No SVG outline yet. Click "Generate SVG" to create one from telemetry GPS data.
                        </div>
                      )}
                    </div>
                    {trackRefImage && (
                      <div style={{ textAlign: "center", marginTop: "4px" }}>
                        <button onClick={() => setTrackRefImage(null)} style={{ background: "none", border: "none", color: "var(--text3)", fontSize: "10px", cursor: "pointer", textDecoration: "underline" }}>Remove reference image</button>
                      </div>
                    )}
                    <p style={{ fontSize: "10px", color: "var(--text3)", marginTop: "4px", textAlign: "center" }}>
                      {trackRefImage ? "Reference image shown behind SVG outline for manual alignment comparison." : "Load a track screenshot from iRacing to compare against the generated outline."}
                    </p>
                  </div>

                  {/* Corners Table */}
                  <div style={{ marginBottom: "12px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                      <label style={{ fontSize: "12px", color: "var(--text2)", fontWeight: 600 }}>Corner Boundaries</label>
                      <button onClick={addCorner} style={{ ...btnStyle, fontSize: "11px", padding: "4px 12px" }}>+ Add Corner</button>
                    </div>
                    {trackCorners.length === 0 ? (
                      <div style={{ padding: "24px", textAlign: "center", color: "var(--text3)", fontSize: "12px", border: "1px dashed var(--border)", borderRadius: "6px" }}>
                        No corners defined. Add corners or generate them from telemetry data.
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "400px", overflowY: "auto" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "40px 1fr 100px 100px 40px", gap: "8px", padding: "6px 8px", fontSize: "10px", color: "var(--text3)", fontWeight: 600, textTransform: "uppercase" }}>
                          <span>#</span><span>Name</span><span>Start %</span><span>End %</span><span></span>
                        </div>
                        {trackCorners.map((corner, i) => (
                          <div key={i} style={{ display: "grid", gridTemplateColumns: "40px 1fr 100px 100px 40px", gap: "8px", alignItems: "center", padding: "4px 8px", background: "rgba(255,255,255,0.02)", borderRadius: "4px" }}>
                            <span style={{ fontSize: "12px", color: "var(--text2)", fontWeight: 600 }}>{corner.number || i + 1}</span>
                            <input
                              type="text" value={corner.name || ""} onChange={e => updateCorner(i, "name", e.target.value)}
                              style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "#fff", padding: "4px 8px", borderRadius: "4px", fontSize: "12px" }}
                            />
                            <input
                              type="number" step="0.001" value={corner.lapDistPctStart ?? ""} onChange={e => updateCorner(i, "lapDistPctStart", parseFloat(e.target.value) || 0)}
                              style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "#fff", padding: "4px 8px", borderRadius: "4px", fontSize: "12px" }}
                            />
                            <input
                              type="number" step="0.001" value={corner.lapDistPctEnd ?? ""} onChange={e => updateCorner(i, "lapDistPctEnd", parseFloat(e.target.value) || 0)}
                              style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "#fff", padding: "4px 8px", borderRadius: "4px", fontSize: "12px" }}
                            />
                            <button onClick={() => removeCorner(i)} style={{ background: "none", border: "none", color: "#f87171", cursor: "pointer", fontSize: "14px", padding: "2px" }} title="Remove corner">&#x2715;</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: "8px" }}>
                    <button onClick={saveTrackCorners} disabled={trackSaving} style={{
                      ...btnStyle, background: "var(--accent)", padding: "8px 20px",
                      opacity: trackSaving ? 0.6 : 1, cursor: trackSaving ? "not-allowed" : "pointer",
                    }}>{trackSaving ? "Saving..." : "Save Boundaries"}</button>
                  </div>
                </div>
              )}

              {/* Track List */}
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {trackModels.map(track => (
                  <div key={track.id} style={itemStyle}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{track.trackName}</div>
                      <div style={{ fontSize: "11px", color: "var(--text2)" }}>
                        ID: {track.trackId} | {track.corners?.length || 0} corners
                        {track.svgOutline && <span style={{ color: "var(--accent)", marginLeft: "8px" }}>SVG</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button onClick={() => generateTrackSvg(track.trackId)} style={{ ...btnStyle, background: "rgba(59, 130, 246, 0.15)", color: "#60a5fa", fontSize: "11px" }} title="Auto-generate SVG outline from GPS telemetry">Gen SVG</button>
                      <button onClick={() => openTrackEditor(track)} style={{ ...btnStyle, background: editingTrack?.trackId === track.trackId ? "var(--accent)" : "rgba(255,255,255,0.08)", color: editingTrack?.trackId === track.trackId ? "#fff" : "var(--text1)" }} >Edit Boundaries</button>
                    </div>
                  </div>
                ))}
                {trackModels.length === 0 && (
                  <div style={{ padding: "32px", textAlign: "center", color: "var(--text2)", border: "1px dashed var(--border)", borderRadius: "8px" }}>
                    <p style={{ fontSize: "13px", marginBottom: "4px" }}>No track models found</p>
                    <p style={{ fontSize: "11px", color: "var(--text3)" }}>Tracks are auto-created when users upload telemetry data.</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {activeTab === "users" && (() => {
            const q = userSearch.toLowerCase();
            const filtered = q ? users.filter(u =>
              (u.displayName || "").toLowerCase().includes(q) ||
              (u.email || "").toLowerCase().includes(q) ||
              (u.plan || "").toLowerCase().includes(q)
            ) : users;

            const handleBanToggle = async (u: any) => {
              const action = u.isActive === false ? "unban" : "ban";
              const ok = await confirm({ message: `Are you sure you want to ${action} ${u.displayName || u.email}?${action === "ban" ? " They will be logged out and unable to access the platform." : ""}`, danger: action === "ban" });
              if (!ok) return;
              try {
                await api(`/api/admin/users/${u.id}`, { method: "PATCH", body: JSON.stringify({ is_active: action !== "ban" }) });
                setUsers(users.map(x => x.id === u.id ? { ...x, isActive: action !== "ban" } : x));
                toast.success(`${u.displayName || u.email} has been ${action === "ban" ? "banned" : "unbanned"}`);
              } catch (err: any) { toast.error(err.message || `Failed to ${action} user`); }
            };

            const handleDelete = async (u: any) => {
              const ok = await confirm({ title: "Delete User", message: `Permanently delete ${u.displayName || u.email}? This cannot be undone.`, danger: true, confirmLabel: "Delete Forever" });
              if (!ok) return;
              try {
                await api(`/api/admin/users/${u.id}`, { method: "DELETE" });
                setUsers(users.filter(x => x.id !== u.id));
                toast.success(`${u.displayName || u.email} has been permanently deleted`);
              } catch (err: any) { toast.error(err.message || "Failed to delete user"); }
            };

            return (
            <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <h3 style={{ fontSize: "18px" }}>User Management</h3>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "12px", color: "var(--text2)" }}>{filtered.length} of {users.length} users</span>
                  <input
                    type="text"
                    placeholder="Search by name, email, or plan..."
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    style={{
                      padding: "6px 12px", width: "260px",
                      background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)",
                      borderRadius: "6px", color: "var(--text1)", fontSize: "12px", outline: "none",
                    }}
                  />
                </div>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "var(--text2)", borderBottom: "1px solid var(--border)" }}>
                    <th style={{ padding: "12px 8px" }}>Status</th>
                    <th style={{ padding: "12px 8px" }}>User</th>
                    <th style={{ padding: "12px 8px" }}>Email</th>
                    <th style={{ padding: "12px 8px" }}>Plan</th>
                    <th style={{ padding: "12px 8px" }}>Sessions</th>
                    <th style={{ padding: "12px 8px" }}>Joined</th>
                    <th style={{ padding: "12px 8px", textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(u => (
                    <tr key={u.id} style={{ borderBottom: "1px solid var(--border)", opacity: u.isActive === false ? 0.5 : 1 }}>
                      <td style={{ padding: "12px 8px" }}>
                        <span style={{
                          display: "inline-block", width: "8px", height: "8px", borderRadius: "50%",
                          background: u.isActive === false ? "var(--bad)" : "var(--good)",
                          boxShadow: u.isActive === false ? "0 0 6px var(--bad)" : "0 0 6px var(--good)",
                        }} title={u.isActive === false ? "Banned" : "Active"} />
                      </td>
                      <td style={{ padding: "12px 8px", fontWeight: 600 }}>
                        {u.displayName || "N/A"}
                        {u.isActive === false && <span style={{ marginLeft: "6px", fontSize: "9px", fontWeight: 700, color: "var(--bad)", textTransform: "uppercase" }}>Banned</span>}
                      </td>
                      <td style={{ padding: "12px 8px", color: "var(--text2)" }}>{u.email}</td>
                      <td style={{ padding: "12px 8px" }}>
                        <select
                          value={u.plan}
                          onChange={async (e) => {
                            const newPlan = e.target.value;
                            if (newPlan === u.plan) return;
                            const label = newPlan === "admin" ? "grant admin privileges to" : `change plan to "${newPlan}" for`;
                            const ok = await confirm({ message: `Are you sure you want to ${label} ${u.displayName || u.email}?`, danger: newPlan === "admin" });
                            if (!ok) return;
                            try {
                              await api(`/api/admin/users/${u.id}`, { method: "PATCH", body: JSON.stringify({ plan: newPlan }) });
                              setUsers(users.map(x => x.id === u.id ? { ...x, plan: newPlan } : x));
                              toast.success(`${u.displayName || u.email} is now ${newPlan}`);
                            } catch (err: any) { toast.error(err.message || "Failed to update plan"); }
                          }}
                          style={{
                            padding: "4px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", cursor: "pointer",
                            background: u.plan === "admin" ? "rgba(245, 158, 11, 0.15)" : u.plan === "pro" ? "var(--accent)" : u.plan?.startsWith("beta") ? "rgba(168, 85, 247, 0.15)" : "var(--border)",
                            color: u.plan === "admin" ? "#f59e0b" : u.plan?.startsWith("beta") ? "#a855f7" : "#fff",
                            border: u.plan === "admin" ? "1px solid rgba(245, 158, 11, 0.3)" : u.plan?.startsWith("beta") ? "1px solid rgba(168, 85, 247, 0.3)" : "1px solid transparent",
                          }}
                        >
                          <option value="free">FREE</option>
                          <option value="pro">PRO</option>
                          <option value="beta_1">BETA 1</option>
                          <option value="beta_2">BETA 2</option>
                          <option value="admin">ADMIN</option>
                        </select>
                      </td>
                      <td style={{ padding: "12px 8px", fontVariantNumeric: "tabular-nums" }}>{u.sessionCount ?? 0}</td>
                      <td style={{ padding: "12px 8px", color: "var(--text2)" }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                      <td style={{ padding: "12px 8px", textAlign: "right" }}>
                        <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
                          <button onClick={() => fetchUserSessions(u)} style={{ ...btnStyle, background: "var(--accent)", fontSize: "11px", padding: "4px 10px" }}>Sessions</button>
                          <button
                            onClick={() => handleBanToggle(u)}
                            style={{
                              ...btnStyle, fontSize: "11px", padding: "4px 10px",
                              background: u.isActive === false ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)",
                              color: u.isActive === false ? "#22c55e" : "#ef4444",
                              border: `1px solid ${u.isActive === false ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`,
                            }}
                          >
                            {u.isActive === false ? "Unban" : "Ban"}
                          </button>
                          <button
                            onClick={() => handleDelete(u)}
                            style={{
                              ...btnStyle, fontSize: "11px", padding: "4px 10px",
                              background: "rgba(239,68,68,0.08)",
                              color: "#ef4444",
                              border: "1px solid rgba(239,68,68,0.2)",
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: "24px", textAlign: "center", color: "var(--text2)" }}>
                      {userSearch ? `No users match "${userSearch}"` : "No users found"}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </section>
            );
          })()}

          {activeTab === "queue" && (
            <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
              <h3 style={{ fontSize: "18px", marginBottom: "20px" }}>Background Processing Queue</h3>
              {/* OT-209: Queue Health Summary */}
              {queueHealth && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "12px", marginBottom: "20px" }}>
                  {[
                    { label: "Queued", val: queueHealth.queued, color: "#fbbf24" },
                    { label: "Processing", val: queueHealth.processing, color: "#60a5fa" },
                    { label: "Complete", val: queueHealth.complete, color: "#4ade80" },
                    { label: "Failed (24h)", val: queueHealth.failed_24h, color: queueHealth.failed_24h > 0 ? "#f87171" : "var(--text3)" },
                    { label: "Avg Duration", val: queueHealth.avg_duration_ms ? `${(queueHealth.avg_duration_ms / 1000).toFixed(1)}s` : "—", color: "var(--text2)" },
                  ].map((s, i) => (
                    <div key={i} style={{ background: "rgba(255,255,255,0.03)", padding: "12px", borderRadius: "8px", textAlign: "center" }}>
                      <div style={{ fontSize: "20px", fontWeight: 700, color: s.color }}>{s.val}</div>
                      <div style={{ fontSize: "10px", color: "var(--text3)", textTransform: "uppercase", fontWeight: 600 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              )}
              {queueHealth?.stale?.length > 0 && (
                <div style={{ background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: "8px", padding: "12px", marginBottom: "16px" }}>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: "#f87171", textTransform: "uppercase", marginBottom: "6px" }}>Stale Jobs (processing &gt; 30 min)</div>
                  {queueHealth.stale.map((s: any) => (
                    <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0" }}>
                      <span style={{ fontSize: "12px", color: "var(--text2)" }}>{s.filename} <span style={{ color: "var(--text3)", fontSize: "10px" }}>({s.id})</span></span>
                      <button onClick={async () => { try { await api(`/api/admin/jobs/${s.id}/reset`, { method: "POST" }); toast.success("Job reset to queued"); const qh = await api<any>("/api/admin/queue-health"); setQueueHealth(qh); } catch { toast.error("Reset failed"); } }} style={{ ...btnStyle, background: "var(--warn)", fontSize: "10px", padding: "3px 8px" }}>Reset</button>
                    </div>
                  ))}
                </div>
              )}
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "var(--text2)", borderBottom: "1px solid var(--border)" }}>
                    <th style={{ padding: "12px 8px" }}>Filename</th>
                    <th style={{ padding: "12px 8px" }}>Status</th>
                    <th style={{ padding: "12px 8px" }}>Started</th>
                    <th style={{ padding: "12px 8px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map(j => (
                    <tr key={j.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "12px 8px" }}>
                        <div style={{ fontWeight: 600 }}>{j.filename}</div>
                        <div style={{ fontSize: "10px", color: "var(--text3)" }}>ID: {j.id}</div>
                      </td>
                      <td style={{ padding: "12px 8px" }}>
                        <span style={{ 
                          padding: "2px 6px", 
                          borderRadius: "4px", 
                          fontSize: "10px", 
                          background: j.status === "complete" ? "rgba(74, 222, 128, 0.1)" : j.status === "failed" ? "rgba(248, 113, 113, 0.1)" : "rgba(251, 191, 36, 0.1)",
                          color: j.status === "complete" ? "#4ade80" : j.status === "failed" ? "#f87171" : "#fbbf24",
                          textTransform: "uppercase",
                          fontWeight: 700
                        }}>{j.status}</span>
                        {j.attemptCount > 1 && <span style={{ fontSize: "9px", color: "var(--warn)", marginLeft: "4px" }}>×{j.attemptCount}</span>}
                        {j.error && <div style={{ fontSize: "10px", color: "#f87171", marginTop: "4px", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={j.error}>{j.error}</div>}
                      </td>
                      <td style={{ padding: "12px 8px", color: "var(--text2)" }}>{new Date(j.createdAt).toLocaleString()}</td>
                      <td style={{ padding: "12px 8px", textAlign: "right" }}>
                        {j.status === "failed" && (
                          <button onClick={() => restartJob(j.id)} style={{ ...btnStyle, background: "var(--accent)" }}>Restart Job</button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {jobs.length === 0 && (
                    <tr><td colSpan={4} style={{ padding: "20px", textAlign: "center", color: "var(--text2)" }}>No background jobs found.</td></tr>
                  )}
                </tbody>
              </table>
            </section>
          )}

          {activeTab === "coaching" && (() => {
            const totalCost = coachingHistory.reduce((sum: number, h: any) => sum + (h.costUsd ?? h.cost_usd ?? 0), 0);
            const avgScore = coachingHistory.filter((h: any) => h.report?.score != null).length > 0
              ? coachingHistory.filter((h: any) => h.report?.score != null).reduce((sum: number, h: any) => sum + h.report.score, 0) / coachingHistory.filter((h: any) => h.report?.score != null).length
              : null;
            const modelCounts: Record<string, number> = {};
            coachingHistory.forEach((h: any) => { const m = h.report?.model || h.model || "unknown"; modelCounts[m] = (modelCounts[m] || 0) + 1; });

            return (
            <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
              <h3 style={{ fontSize: "18px", marginBottom: "4px" }}>Coaching Analytics</h3>
              <p style={{ fontSize: "12px", color: "var(--text2)", marginBottom: "20px" }}>AI coaching report history across all users.</p>

              {/* Summary Stats */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "12px", marginBottom: "24px" }}>
                <div style={{ padding: "12px", background: "rgba(255,255,255,0.02)", borderRadius: "8px", border: "1px solid var(--border)" }}>
                  <div style={{ fontSize: "11px", color: "var(--text3)", marginBottom: "4px" }}>Total Reports</div>
                  <div style={{ fontSize: "20px", fontWeight: 700 }}>{coachingHistory.length}</div>
                </div>
                <div style={{ padding: "12px", background: "rgba(255,255,255,0.02)", borderRadius: "8px", border: "1px solid var(--border)" }}>
                  <div style={{ fontSize: "11px", color: "var(--text3)", marginBottom: "4px" }}>Total AI Cost</div>
                  <div style={{ fontSize: "20px", fontWeight: 700 }}>${totalCost.toFixed(2)}</div>
                </div>
                <div style={{ padding: "12px", background: "rgba(255,255,255,0.02)", borderRadius: "8px", border: "1px solid var(--border)" }}>
                  <div style={{ fontSize: "11px", color: "var(--text3)", marginBottom: "4px" }}>Avg Sense Score</div>
                  <div style={{ fontSize: "20px", fontWeight: 700, color: "var(--accent)" }}>{avgScore != null ? `${Math.round(avgScore)}` : "—"}</div>
                </div>
                <div style={{ padding: "12px", background: "rgba(255,255,255,0.02)", borderRadius: "8px", border: "1px solid var(--border)" }}>
                  <div style={{ fontSize: "11px", color: "var(--text3)", marginBottom: "4px" }}>Models Used</div>
                  <div style={{ fontSize: "12px", fontWeight: 600 }}>{Object.entries(modelCounts).map(([m, c]) => `${m} (${c})`).join(", ") || "—"}</div>
                </div>
              </div>

              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "var(--text2)", borderBottom: "1px solid var(--border)" }}>
                    <th style={{ padding: "12px 8px" }}>Date</th>
                    <th style={{ padding: "12px 8px" }}>Model</th>
                    <th style={{ padding: "12px 8px" }}>Tier</th>
                    <th style={{ padding: "12px 8px" }}>Cost</th>
                    <th style={{ padding: "12px 8px" }}>User</th>
                    <th style={{ padding: "12px 8px" }}>Sense Score</th>
                  </tr>
                </thead>
                <tbody>
                  {coachingHistory.map((h: any) => {
                    const score = h.report?.score;
                    const tier = h.report?.tier || h.tier || "—";
                    const model = h.report?.model || h.model || "—";
                    return (
                    <tr key={h.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "12px 8px" }}>{new Date(h.createdAt ?? h.created_at).toLocaleString()}</td>
                      <td style={{ padding: "12px 8px", color: "var(--text2)" }}>{model}</td>
                      <td style={{ padding: "12px 8px" }}>
                        <span style={{
                          fontSize: "10px", padding: "2px 6px", borderRadius: "3px", fontWeight: 600,
                          background: tier === "pro" ? "rgba(59, 130, 246, 0.15)" : "rgba(255,255,255,0.06)",
                          color: tier === "pro" ? "#60a5fa" : "var(--text2)",
                        }}>{tier}</span>
                      </td>
                      <td style={{ padding: "12px 8px", fontVariantNumeric: "tabular-nums" }}>${(h.costUsd ?? h.cost_usd ?? 0).toFixed(4)}</td>
                      <td style={{ padding: "12px 8px" }}>{(h.userId ?? h.user_id)?.substring(0, 8)}...</td>
                      <td style={{ padding: "12px 8px", fontWeight: 700, color: score != null ? "var(--accent)" : "var(--text3)" }}>
                        {score != null ? score : "—"}
                      </td>
                    </tr>
                    );
                  })}
                  {coachingHistory.length === 0 && (
                    <tr><td colSpan={6} style={{ padding: "32px", textAlign: "center", color: "var(--text2)" }}>No coaching reports generated yet.</td></tr>
                  )}
                </tbody>
              </table>
            </section>
            );
          })()}

          {activeTab === "support" && (
            <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <div>
                  <h3 style={{ fontSize: "18px" }}>Support Tickets</h3>
                  <p style={{ color: "var(--text2)", fontSize: "12px" }}>
                    {supportTickets.length} total — {supportTickets.filter(t => t.status === "open").length} open, {supportTickets.filter(t => t.status === "in_progress").length} in progress
                  </p>
                </div>
              </div>

              {supportTickets.length === 0 ? (
                <div style={{ padding: "40px", textAlign: "center", color: "var(--text2)" }}>No support tickets yet.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {supportTickets.map(ticket => {
                    const isEditing = editingTicket === ticket.id;
                    const edits = ticketEdits[ticket.id] || {};
                    const pendingDrafts = (ticket.drafts || []).filter((draft: any) => draft.status === "pending");
                    const statusColors: Record<string, string> = { open: "#f59e0b", in_progress: "#3b82f6", closed: "#22c55e" };
                    const priorityColors: Record<string, string> = { low: "var(--text3)", normal: "var(--text2)", high: "#ef4444" };
                    return (
                      <div key={ticket.id} style={{ background: "rgba(255,255,255,0.02)", borderRadius: "8px", border: "1px solid var(--border)", padding: "16px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px" }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                              <span style={{ fontWeight: 700, fontSize: "14px" }}>{ticket.subject}</span>
                              <span style={{ fontSize: "10px", padding: "2px 8px", borderRadius: "10px", background: statusColors[ticket.status] || "#666", color: "#000", fontWeight: 700, textTransform: "uppercase" }}>
                                {ticket.status?.replace("_", " ")}
                              </span>
                              <span style={{ fontSize: "10px", padding: "2px 6px", borderRadius: "10px", border: `1px solid ${priorityColors[ticket.priority] || "var(--border)"}`, color: priorityColors[ticket.priority] || "var(--text2)", fontWeight: 600 }}>
                                {ticket.priority}
                              </span>
                            </div>
                            <div style={{ fontSize: "12px", color: "var(--text2)", marginBottom: "8px" }}>
                              {ticket.user?.displayName || ticket.user?.email || "Unknown user"} — {new Date(ticket.createdAt).toLocaleString()}
                            </div>
                            {(() => {
                              const { text, screenshots } = parseTicketBody(ticket.body);
                              return (
                                <div style={{ fontSize: "13px", color: "var(--text1)", lineHeight: 1.5 }}>
                                  <div style={{ whiteSpace: "pre-wrap" }}>{text}</div>
                                  <TicketAttachments urls={screenshots} />
                                </div>
                              );
                            })()}

                            {/* Conversation thread [OT-223H] */}
                            {(ticket.messages?.length > 0) && (
                              <div style={{ marginTop: "12px" }}>
                                <div style={{ fontSize: "10px", color: "var(--text3)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                                  Conversation ({ticket.messages.length})
                                </div>
                                <div style={{ maxHeight: "300px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "6px" }}>
                                  {ticket.messages.map((msg: any) => {
                                    const isAdmin = msg.senderType === "admin";
                                    return (
                                      <div key={msg.id} style={{
                                        padding: "8px 10px",
                                        borderRadius: isAdmin ? "2px 8px 8px 8px" : "8px 2px 8px 8px",
                                        background: isAdmin ? "rgba(59,130,246,0.1)" : "rgba(255,255,255,0.04)",
                                        border: `1px solid ${isAdmin ? "rgba(59,130,246,0.2)" : "var(--border)"}`,
                                        alignSelf: isAdmin ? "flex-start" : "flex-end",
                                        maxWidth: "90%"
                                      }}>
                                        <div style={{ fontSize: "9px", fontWeight: 700, color: isAdmin ? "#60a5fa" : "var(--text3)", marginBottom: "2px", textTransform: "uppercase" }}>
                                          {isAdmin ? (msg.sender?.displayName || "Admin") : (ticket.user?.displayName || "User")}
                                          <span style={{ fontWeight: 400, marginLeft: "8px", textTransform: "none" }}>
                                            {new Date(msg.createdAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                                          </span>
                                        </div>
                                        <div style={{ fontSize: "12px", color: "var(--text1)", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{msg.body}</div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {pendingDrafts.length > 0 && (
                              <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
                                <div style={{ fontSize: "10px", color: "#8b5cf6", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                  Pending Agent Drafts ({pendingDrafts.length})
                                </div>
                                {pendingDrafts.map((draft: any) => (
                                  <div
                                    key={draft.id}
                                    style={{
                                      padding: "12px",
                                      background: "rgba(139,92,246,0.08)",
                                      borderRadius: "8px",
                                      border: "1px solid rgba(139,92,246,0.22)",
                                    }}
                                  >
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
                                      <div style={{ fontSize: "11px", color: "#c4b5fd", fontWeight: 700 }}>
                                        Draft from {draft.agentName || "agent"}
                                        {draft.category ? ` • ${draft.category}` : ""}
                                      </div>
                                      <div style={{ fontSize: "10px", color: "var(--text3)" }}>
                                        {new Date(draft.createdAt).toLocaleString()}
                                      </div>
                                    </div>
                                    {draft.notes && (
                                      <div style={{ fontSize: "11px", color: "var(--text2)", marginBottom: "8px", whiteSpace: "pre-wrap" }}>
                                        {draft.notes}
                                      </div>
                                    )}
                                    <textarea
                                      style={{ ...inputStyle, minHeight: "96px", fontFamily: "inherit", marginBottom: "10px" }}
                                      value={draftEdits[draft.id] ?? draft.body}
                                      onChange={e => setDraftEdits(prev => ({ ...prev, [draft.id]: e.target.value }))}
                                    />
                                    <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                                      <button
                                        onClick={() => rejectDraft(ticket.id, draft.id)}
                                        disabled={draftActionId === draft.id}
                                        style={{ ...btnStyle, background: "rgba(239,68,68,0.15)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.3)", opacity: draftActionId === draft.id ? 0.7 : 1 }}
                                      >
                                        Reject
                                      </button>
                                      <button
                                        onClick={() => approveDraft(ticket.id, draft)}
                                        disabled={draftActionId === draft.id || !(draftEdits[draft.id] ?? draft.body).trim()}
                                        style={{ ...btnStyle, background: "rgba(34,197,94,0.15)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.3)", opacity: draftActionId === draft.id ? 0.7 : 1 }}
                                      >
                                        {draftActionId === draft.id ? "Working..." : "Approve & Send"}
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Legacy admin notes (if no messages yet) */}
                            {ticket.adminNotes && !(ticket.messages?.length > 0) && !isEditing && (
                              <div style={{ marginTop: "12px", padding: "10px", background: "rgba(59,130,246,0.08)", borderRadius: "6px", border: "1px solid rgba(59,130,246,0.2)" }}>
                                <div style={{ fontSize: "10px", color: "#3b82f6", fontWeight: 700, textTransform: "uppercase", marginBottom: "4px" }}>Admin Notes</div>
                                <div style={{ fontSize: "12px", color: "var(--text1)", whiteSpace: "pre-wrap" }}>{ticket.adminNotes}</div>
                              </div>
                            )}

                            {/* Quick reply [OT-223H] */}
                            {!isEditing && ticket.status !== "closed" && (
                              <AdminReplyInput
                                ticketId={ticket.id}
                                onSent={(msg) => {
                                  setSupportTickets(supportTickets.map(t => {
                                    if (t.id !== ticket.id) return t;
                                    const msgs = t.messages || [];
                                    return { ...t, messages: [...msgs, msg], status: t.status === "open" ? "in_progress" : t.status };
                                  }));
                                }}
                              />
                            )}
                          </div>
                          <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                            {!isEditing ? (
                              <>
                                <button onClick={() => startEditTicket(ticket)} style={btnStyle}>Edit</button>
                                <button onClick={() => deleteTicket(ticket.id)} style={{ ...btnStyle, background: "rgba(239,68,68,0.15)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.3)" }}>Delete</button>
                              </>
                            ) : (
                              <>
                                <button onClick={() => updateTicket(ticket.id)} style={{ ...btnStyle, background: "var(--accent)", color: "#000" }}>Save</button>
                                <button onClick={() => { setEditingTicket(null); }} style={btnStyle}>Cancel</button>
                              </>
                            )}
                          </div>
                        </div>

                        {isEditing && (
                          <div style={{ marginTop: "16px", padding: "16px", background: "rgba(255,255,255,0.02)", borderRadius: "8px", border: "1px solid var(--border)" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                              <div>
                                <label style={labelStyle}>Status</label>
                                <Select
                                  value={edits.status || ticket.status}
                                  onChange={v => setTicketEdits({ ...ticketEdits, [ticket.id]: { ...edits, status: v } })}
                                  options={[
                                    { value: "open", label: "Open" },
                                    { value: "in_progress", label: "In Progress" },
                                    { value: "closed", label: "Closed" },
                                  ]}
                                />
                              </div>
                              <div>
                                <label style={labelStyle}>Priority</label>
                                <Select
                                  value={edits.priority || ticket.priority}
                                  onChange={v => setTicketEdits({ ...ticketEdits, [ticket.id]: { ...edits, priority: v } })}
                                  options={[
                                    { value: "low", label: "Low" },
                                    { value: "normal", label: "Normal" },
                                    { value: "high", label: "High" },
                                  ]}
                                />
                              </div>
                            </div>
                            <div>
                              <label style={labelStyle}>Admin Notes</label>
                              <textarea
                                style={{ ...inputStyle, minHeight: "80px", fontFamily: "inherit" }}
                                value={edits.adminNotes ?? ticket.adminNotes ?? ""}
                                onChange={e => setTicketEdits({ ...ticketEdits, [ticket.id]: { ...edits, adminNotes: e.target.value } })}
                                placeholder="Internal notes, resolution details..."
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* User Session Lookup (existing support access) */}
              {selectedUser && (
                <div style={{ marginTop: "32px", paddingTop: "24px", borderTop: "1px solid var(--border)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                    <div>
                      <h4 style={{ fontSize: "14px" }}>Session Lookup: {selectedUser.displayName}</h4>
                      <p style={{ color: "var(--text2)", fontSize: "11px" }}>Viewing session history for troubleshooting.</p>
                    </div>
                    <button onClick={() => setSelectedUser(null)} style={btnStyle}>Close</button>
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: "var(--text2)", borderBottom: "1px solid var(--border)" }}>
                        <th style={{ padding: "12px 8px" }}>Track</th>
                        <th style={{ padding: "12px 8px" }}>Car</th>
                        <th style={{ padding: "12px 8px" }}>Date</th>
                        <th style={{ padding: "12px 8px" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedUserSessions.map(s => (
                        <tr key={s.id} style={{ borderBottom: "1px solid var(--border)" }}>
                          <td style={{ padding: "12px 8px", fontWeight: 600 }}>{s.trackName}</td>
                          <td style={{ padding: "12px 8px" }}>{s.carName}</td>
                          <td style={{ padding: "12px 8px", color: "var(--text2)" }}>{new Date(s.sessionDate).toLocaleString()}</td>
                          <td style={{ padding: "12px 8px", textAlign: "right" }}>
                            <Link to={`/session/${s.id}`} style={{ color: "var(--accent)", textDecoration: "none", fontWeight: 600 }}>View</Link>
                          </td>
                        </tr>
                      ))}
                      {selectedUserSessions.length === 0 && (
                        <tr><td colSpan={4} style={{ padding: "20px", textAlign: "center", color: "var(--text2)" }}>No sessions found.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

        </div>
      )}
    </div>
  );
}

function Tab({ active, onClick, label }: { active: boolean, onClick: () => void, label: string }) {
  return (
    <button 
      onClick={onClick}
      style={{
        padding: "12px 0",
        background: "none",
        border: "none",
        borderBottom: active ? "2px solid var(--accent)" : "2px solid transparent",
        color: active ? "var(--text1)" : "var(--text2)",
        fontWeight: 600,
        fontSize: "14px",
        cursor: "pointer",
        transition: "all 0.2s"
      }}
    >
      {label}
    </button>
  );
}

function StatCard({ label, value }: { label: string, value: string | number }) {
  return (
    <div style={{ background: "var(--card)", padding: "20px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
      <div style={{ color: "var(--text2)", fontSize: "12px", textTransform: "uppercase", marginBottom: "8px", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: "24px", fontWeight: 700, letterSpacing: "-0.025em" }}>{value}</div>
    </div>
  );
}

const itemStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "12px",
  background: "rgba(255,255,255,0.02)",
  borderRadius: "4px",
  fontSize: "13px"
};

const btnStyle = {
  padding: "6px 12px",
  background: "rgba(255, 255, 255, 0.08)",
  border: "none",
  color: "var(--text1)",
  borderRadius: "4px",
  fontSize: "11px",
  cursor: "pointer"
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "10px",
  color: "var(--text3)",
  textTransform: "uppercase",
  marginBottom: "6px",
  fontWeight: 700
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px",
  background: "var(--bg)",
  border: "1px solid var(--border)",
  borderRadius: "6px",
  color: "#fff",
  fontSize: "13px"
};

/** Admin quick-reply input for ticket conversations [OT-223H] */
function AdminReplyInput({ ticketId, onSent }: { ticketId: string; onSent: (msg: any) => void }) {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      const msg = await api<any>(`/api/admin/support/tickets/${ticketId}/messages`, {
        method: "POST",
        body: JSON.stringify({ body: reply.trim() })
      });
      onSent(msg);
      setReply("");
    } catch {
      // silent
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ marginTop: "10px", display: "flex", gap: "6px" }}>
      <input
        type="text"
        value={reply}
        onChange={e => setReply(e.target.value)}
        onKeyDown={e => e.key === "Enter" && send()}
        placeholder="Reply to user..."
        style={{ flex: 1, padding: "8px 10px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "6px", color: "#fff", fontSize: "12px", outline: "none" }}
      />
      <button
        onClick={send}
        disabled={sending || !reply.trim()}
        style={{ padding: "8px 12px", background: "var(--accent)", color: "#fff", border: "none", borderRadius: "6px", fontWeight: 600, fontSize: "11px", cursor: sending ? "wait" : "pointer", opacity: !reply.trim() ? 0.5 : 1 }}
      >
        {sending ? "..." : "Reply"}
      </button>
    </div>
  );
}
